import { useState, useEffect, useCallback } from 'react'
import { getNexusClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth/context'
import { generateRoomId, generateRoomLink } from '@/lib/livekit/tokenService'
import {
  PublicBookingPage,
  PublicBookingPageRow,
  PublicBooking,
  PublicBookingRow,
  CreateBookingPageInput,
  CreateBookingInput,
  bookingPageRowToBookingPage,
  bookingRowToBooking,
} from '@/types/calendar'

// ============================================================================
// useBookingPage - Fetch booking page by slug (public, no auth required)
// ============================================================================

export function useBookingPage(slug: string | null) {
  const [bookingPage, setBookingPage] = useState<PublicBookingPage | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) {
      setBookingPage(null)
      return
    }

    async function fetchBookingPage() {
      setIsLoading(true)
      setError(null)

      try {
        const client = getNexusClient()

        // Fetch booking page with user info
        const { data, error: fetchError } = await (client
          .from('public_booking_pages')
          .select(`
            *,
            admin_users:admin_user_id (
              id,
              email,
              first_name,
              last_name,
              avatar_url
            )
          `)
          .eq('slug', slug as string)
          .eq('is_active', true)
          .single() as any)

        if (fetchError) {
          if (fetchError.code === 'PGRST116') {
            setError('Booking page not found')
          } else {
            throw fetchError
          }
          return
        }

        const row = data as PublicBookingPageRow & { admin_users?: any }
        const page = bookingPageRowToBookingPage(row)

        if (row.admin_users) {
          page.user = {
            id: row.admin_users.id,
            email: row.admin_users.email,
            firstName: row.admin_users.first_name,
            lastName: row.admin_users.last_name,
          }
          // Use admin user's avatar if page doesn't have one
          if (!page.avatarUrl && row.admin_users.avatar_url) {
            page.avatarUrl = row.admin_users.avatar_url
          }
        }

        setBookingPage(page)
      } catch (err: any) {
        console.error('Error fetching booking page:', err)
        setError(err.message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchBookingPage()
  }, [slug])

  return { bookingPage, isLoading, error }
}

// ============================================================================
// useBookingPages - List user's booking pages
// ============================================================================

export function useBookingPages() {
  const { adminUser } = useAuth()
  const [bookingPages, setBookingPages] = useState<PublicBookingPage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchBookingPages = useCallback(async () => {
    if (!adminUser?.id) {
      setBookingPages([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { data, error: fetchError } = await (client
        .from('public_booking_pages')
        .select('*')
        .eq('admin_user_id', adminUser.id)
        .order('created_at', { ascending: false }) as any)

      if (fetchError) throw fetchError

      setBookingPages((data || []).map(bookingPageRowToBookingPage))
    } catch (err: any) {
      console.error('Error fetching booking pages:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [adminUser])

  useEffect(() => {
    fetchBookingPages()
  }, [fetchBookingPages])

  return {
    bookingPages,
    isLoading,
    error,
    refetch: fetchBookingPages,
  }
}

// ============================================================================
// useBookingPageMutations - Create/update/delete booking pages
// ============================================================================

export function useBookingPageMutations() {
  const { adminUser } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Create a new booking page
  const createBookingPage = useCallback(async (
    input: CreateBookingPageInput
  ): Promise<PublicBookingPage | null> => {
    if (!adminUser?.id) {
      setError('You must be logged in to create booking pages')
      return null
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      // Validate slug format
      const slugRegex = /^[a-z0-9-]+$/
      if (!slugRegex.test(input.slug)) {
        throw new Error('Slug can only contain lowercase letters, numbers, and hyphens')
      }

      const { data, error: insertError } = await (client as any)
        .from('public_booking_pages')
        .insert({
          admin_user_id: adminUser.id,
          slug: input.slug,
          title: input.title || 'Book a Meeting',
          description: input.description || null,
          default_duration: input.defaultDuration || 30,
          buffer_before: input.bufferBefore || 0,
          buffer_after: input.bufferAfter || 15,
          min_notice_hours: input.minNoticeHours || 24,
          max_days_ahead: input.maxDaysAhead || 30,
          allow_video_conference: input.allowVideoConference ?? true,
          require_video_conference: input.requireVideoConference ?? false,
          is_active: true,
        })
        .select()
        .single()

      if (insertError) {
        if (insertError.code === '23505') {
          throw new Error('This URL slug is already taken')
        }
        throw insertError
      }

      return data ? bookingPageRowToBookingPage(data) : null
    } catch (err: any) {
      console.error('Error creating booking page:', err)
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [adminUser])

  // Update a booking page
  const updateBookingPage = useCallback(async (
    id: string,
    input: Partial<CreateBookingPageInput>
  ): Promise<PublicBookingPage | null> => {
    if (!adminUser?.id) {
      setError('You must be logged in to update booking pages')
      return null
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const updateData: Partial<PublicBookingPageRow> = {}

      if (input.slug !== undefined) {
        const slugRegex = /^[a-z0-9-]+$/
        if (!slugRegex.test(input.slug)) {
          throw new Error('Slug can only contain lowercase letters, numbers, and hyphens')
        }
        updateData.slug = input.slug
      }
      if (input.title !== undefined) updateData.title = input.title
      if (input.description !== undefined) updateData.description = input.description || null
      if (input.defaultDuration !== undefined) updateData.default_duration = input.defaultDuration
      if (input.bufferBefore !== undefined) updateData.buffer_before = input.bufferBefore
      if (input.bufferAfter !== undefined) updateData.buffer_after = input.bufferAfter
      if (input.minNoticeHours !== undefined) updateData.min_notice_hours = input.minNoticeHours
      if (input.maxDaysAhead !== undefined) updateData.max_days_ahead = input.maxDaysAhead
      if (input.allowVideoConference !== undefined) updateData.allow_video_conference = input.allowVideoConference
      if (input.requireVideoConference !== undefined) updateData.require_video_conference = input.requireVideoConference

      const { data, error: updateError } = await (client as any)
        .from('public_booking_pages')
        .update(updateData)
        .eq('id', id)
        .eq('admin_user_id', adminUser.id)
        .select()
        .single()

      if (updateError) throw updateError

      return data ? bookingPageRowToBookingPage(data) : null
    } catch (err: any) {
      console.error('Error updating booking page:', err)
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [adminUser])

  // Toggle booking page active status
  const toggleBookingPage = useCallback(async (
    id: string,
    isActive: boolean
  ): Promise<boolean> => {
    if (!adminUser?.id) {
      setError('You must be logged in')
      return false
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { error: updateError } = await (client as any)
        .from('public_booking_pages')
        .update({ is_active: isActive })
        .eq('id', id)
        .eq('admin_user_id', adminUser.id)

      if (updateError) throw updateError

      return true
    } catch (err: any) {
      console.error('Error toggling booking page:', err)
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [adminUser])

  // Delete a booking page
  const deleteBookingPage = useCallback(async (id: string): Promise<boolean> => {
    if (!adminUser?.id) {
      setError('You must be logged in to delete booking pages')
      return false
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { error: deleteError } = await (client
        .from('public_booking_pages')
        .delete()
        .eq('id', id)
        .eq('admin_user_id', adminUser.id) as any)

      if (deleteError) throw deleteError

      return true
    } catch (err: any) {
      console.error('Error deleting booking page:', err)
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [adminUser])

  return {
    createBookingPage,
    updateBookingPage,
    toggleBookingPage,
    deleteBookingPage,
    isLoading,
    error,
  }
}

// ============================================================================
// useBookings - List bookings for a page
// ============================================================================

export function useBookings(bookingPageId?: string) {
  const { adminUser } = useAuth()
  const [bookings, setBookings] = useState<PublicBooking[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchBookings = useCallback(async () => {
    if (!adminUser?.id) {
      setBookings([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      let query = (client
        .from('public_bookings')
        .select(`
          *,
          public_booking_pages!inner (
            admin_user_id
          )
        `)
        .eq('public_booking_pages.admin_user_id', adminUser.id)
        .order('scheduled_start', { ascending: false }) as any)

      if (bookingPageId) {
        query = query.eq('booking_page_id', bookingPageId)
      }

      const { data, error: fetchError } = await query

      if (fetchError) throw fetchError

      setBookings((data || []).map((row: any) => bookingRowToBooking(row)))
    } catch (err: any) {
      console.error('Error fetching bookings:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [adminUser, bookingPageId])

  useEffect(() => {
    fetchBookings()
  }, [fetchBookings])

  // Filter helpers
  const upcomingBookings = bookings.filter(b =>
    b.status === 'confirmed' && new Date(b.scheduledStart) > new Date()
  )

  const pastBookings = bookings.filter(b =>
    new Date(b.scheduledStart) <= new Date()
  )

  return {
    bookings,
    upcomingBookings,
    pastBookings,
    isLoading,
    error,
    refetch: fetchBookings,
  }
}

// ============================================================================
// useCreateBooking - Create a new booking (public, no auth required)
// ============================================================================

export function useCreateBooking() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createBooking = useCallback(async (
    input: CreateBookingInput
  ): Promise<PublicBooking | null> => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      // Fetch booking page to get settings
      const { data: pageData, error: pageError } = await (client
        .from('public_booking_pages')
        .select('*, admin_users:admin_user_id (id, email)')
        .eq('id', input.bookingPageId)
        .eq('is_active', true)
        .single() as any)

      if (pageError || !pageData) {
        throw new Error('Booking page not found or inactive')
      }

      const page = pageData as PublicBookingPageRow & { admin_users?: any }

      // Calculate duration
      const startTime = new Date(input.scheduledStart)
      const endTime = new Date(input.scheduledEnd)
      const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / 60000)

      // Validate duration
      if (durationMinutes !== page.default_duration) {
        throw new Error(`Invalid duration. Must be ${page.default_duration} minutes.`)
      }

      // Check minimum notice
      const now = new Date()
      const hoursUntilMeeting = (startTime.getTime() - now.getTime()) / (1000 * 60 * 60)
      if (hoursUntilMeeting < page.min_notice_hours) {
        throw new Error(`Booking must be at least ${page.min_notice_hours} hours in advance`)
      }

      // Generate meeting room
      let meetingRoomId: string | undefined
      let meetingRoomLink: string | undefined

      if (page.allow_video_conference || page.require_video_conference) {
        meetingRoomId = generateRoomId()
        meetingRoomLink = generateRoomLink(meetingRoomId)
      }

      // Create booking
      const { data: bookingData, error: bookingError } = await (client as any)
        .from('public_bookings')
        .insert({
          booking_page_id: input.bookingPageId,
          booker_email: input.bookerEmail,
          booker_name: input.bookerName,
          booker_notes: input.bookerNotes || null,
          scheduled_start: input.scheduledStart,
          scheduled_end: input.scheduledEnd,
          duration_minutes: durationMinutes,
          meeting_room_id: meetingRoomId || null,
          meeting_room_link: meetingRoomLink || null,
          status: 'confirmed',
        })
        .select()
        .single() as { data: PublicBookingRow | null; error: any }

      if (bookingError) throw bookingError

      // Create calendar event for the host
      if (page.admin_users?.id) {
        const { error: eventError } = await (client as any)
          .from('calendar_events')
          .insert({
            owner_id: page.admin_users.id,
            title: `Meeting with ${input.bookerName}`,
            description: input.bookerNotes || `Booked via ${page.title}`,
            start_time: input.scheduledStart,
            end_time: input.scheduledEnd,
            has_video_conference: !!meetingRoomId,
            meeting_room_id: meetingRoomId || null,
            meeting_room_link: meetingRoomLink || null,
            event_type: 'meeting',
            color: 'blue',
            booking_id: bookingData?.id,
          })

        if (eventError) {
          console.error('Failed to create calendar event:', eventError)
        }

        // Add booker as external attendee
        if (bookingData) {
          const { data: eventData } = await (client as any)
            .from('calendar_events')
            .select('id')
            .eq('booking_id', bookingData.id)
            .single() as { data: { id: string } | null }

          if (eventData) {
            await (client as any)
              .from('calendar_event_attendees')
              .insert([
                {
                  event_id: eventData.id,
                  admin_user_id: page.admin_users.id,
                  response_status: 'accepted',
                  is_organizer: true,
                },
                {
                  event_id: eventData.id,
                  external_email: input.bookerEmail,
                  external_name: input.bookerName,
                  response_status: 'accepted',
                },
              ])
          }
        }
      }

      // TODO: Send confirmation emails (implement via edge function)
      // For now, mark confirmation as sent
      if (bookingData) {
        await (client as any)
          .from('public_bookings')
          .update({ confirmation_sent_at: new Date().toISOString() })
          .eq('id', bookingData.id)
      }

      return bookingData ? bookingRowToBooking(bookingData) : null
    } catch (err: any) {
      console.error('Error creating booking:', err)
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    createBooking,
    isLoading,
    error,
  }
}

// ============================================================================
// useCancelBooking - Cancel a booking (using cancel token)
// ============================================================================

export function useCancelBooking() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cancelBooking = useCallback(async (
    bookingId: string,
    cancelToken: string,
    reason?: string
  ): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { error: updateError } = await (client as any)
        .from('public_bookings')
        .update({
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancellation_reason: reason || null,
        })
        .eq('id', bookingId)
        .eq('cancel_token', cancelToken)
        .eq('status', 'confirmed')

      if (updateError) throw updateError

      // Also cancel the associated calendar event
      const { data: booking } = await (client as any)
        .from('public_bookings')
        .select('id')
        .eq('id', bookingId)
        .single()

      if (booking) {
        await (client as any)
          .from('calendar_events')
          .delete()
          .eq('booking_id', bookingId)
      }

      return true
    } catch (err: any) {
      console.error('Error cancelling booking:', err)
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Host can cancel without token
  const cancelBookingAsHost = useCallback(async (
    bookingId: string,
    reason?: string
  ): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { error: updateError } = await (client as any)
        .from('public_bookings')
        .update({
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancellation_reason: reason || null,
        })
        .eq('id', bookingId)
        .eq('status', 'confirmed')

      if (updateError) throw updateError

      // Delete associated calendar event
      await (client as any)
        .from('calendar_events')
        .delete()
        .eq('booking_id', bookingId)

      return true
    } catch (err: any) {
      console.error('Error cancelling booking:', err)
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    cancelBooking,
    cancelBookingAsHost,
    isLoading,
    error,
  }
}

// Export types
export type { PublicBookingPage, PublicBooking, CreateBookingPageInput, CreateBookingInput }
